import Employee from '@src/Objects/Employee';

import { EMessageType, type IMessage, type IMessageDraft, IMessageThread } from '../types/IMessage';

class MockMessageService {
    private storageKey = 'skill-app-messages';
    private draftsKey = 'skill-app-message-drafts';

    private mockEmployee = new Employee({
        id: 1,
        title: 'Max Mustermann',
        version: 1,
        firstName: 'Max',
        lastName: 'Mustermann',
        gender: 'male' as any,
        description: 'Test Employee',
        email: 'max.mustermann@company.com',
        pictureId: null,
        skills: [],
        roles: [],
        certificates: [],
        competences: [],
        similarUsers: [],
        availability: { availability: 100, workDayCount: 5 },
        products: [],
        projects: [],
        opportunities: [],
        salesIdea: '',
        superPowerOrder: [],
    });

    private mockMessages: IMessage[] = [
        {
            id: '1',
            type: EMessageType.DIRECT,
            from: this.mockEmployee,
            to: [],
            subject: 'Willkommen im neuen Messaging-System',
            content: 'Hallo! Das ist eine Testnachricht für das neue interne Messaging-System.',
            timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
            read: false,
            priority: 'normal',
        },
        {
            id: '2',
            type: EMessageType.SYSTEM,
            from: 'system',
            to: [],
            subject: 'System-Update verfügbar',
            content: 'Ein neues System-Update ist verfügbar. Bitte aktualisieren Sie Ihr Profil.',
            timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
            read: true,
            priority: 'high',
        },
        {
            id: '3',
            type: EMessageType.NOTIFICATION,
            from: 'system',
            to: [],
            subject: 'Skill-Vorschlag erhalten',
            content: 'Basierend auf Ihrem Profil haben wir neue Skill-Vorschläge für Sie.',
            timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
            read: false,
            priority: 'normal',
        },
        {
            id: '4',
            type: EMessageType.AGENT,
            from: 'system',
            to: [],
            subject: 'Code-Review Ergebnisse verfügbar',
            content:
                'Ihr Q Developer Agent hat die Code-Analyse abgeschlossen. 3 Verbesserungsvorschläge wurden identifiziert.',
            timestamp: new Date(Date.now() - 30 * 60 * 1000),
            read: false,
            priority: 'normal',
        },
        {
            id: '5',
            type: EMessageType.AGENT,
            from: 'system',
            to: [],
            subject: 'Automatische Dokumentation erstellt',
            content: 'Der Agent hat automatisch Dokumentation für Ihre neuen API-Endpunkte generiert.',
            timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000),
            read: true,
            priority: 'normal',
        },
    ];

    async getMessages(): Promise<IMessage[]> {
        const stored = localStorage.getItem(this.storageKey);
        const storedMessages = stored ? JSON.parse(stored) : [];
        return [...this.mockMessages, ...storedMessages].sort(
            (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
        );
    }

    async getInboxMessages(): Promise<IMessage[]> {
        const messages = await this.getMessages();
        return messages;
    }

    async getSentMessages(): Promise<IMessage[]> {
        const stored = localStorage.getItem(this.storageKey);
        const storedMessages = stored ? JSON.parse(stored) : [];
        return storedMessages;
    }

    async getSystemMessages(): Promise<IMessage[]> {
        const messages = await this.getMessages();
        return messages.filter(
            (msg) =>
                msg.type === EMessageType.SYSTEM ||
                msg.type === EMessageType.NOTIFICATION ||
                msg.type === EMessageType.AGENT,
        );
    }

    async sendMessage(draft: IMessageDraft): Promise<void> {
        const newMessage: IMessage = {
            id: Date.now().toString(),
            type: EMessageType.DIRECT,
            from: 'current-user' as any,
            to: draft.to,
            subject: draft.subject,
            content: draft.content,
            timestamp: new Date(),
            read: true,
            priority: 'normal',
        };

        const stored = localStorage.getItem(this.storageKey);
        const messages = stored ? JSON.parse(stored) : [];
        messages.push(newMessage);
        localStorage.setItem(this.storageKey, JSON.stringify(messages));
    }

    async markAsRead(messageId: string): Promise<void> {
        const stored = localStorage.getItem(this.storageKey);
        const messages = stored ? JSON.parse(stored) : [];
        const messageIndex = messages.findIndex((msg: IMessage) => msg.id === messageId);

        if (messageIndex !== -1) {
            messages[messageIndex].read = true;
            localStorage.setItem(this.storageKey, JSON.stringify(messages));
        }
    }

    async saveDraft(draft: IMessageDraft): Promise<void> {
        localStorage.setItem(this.draftsKey, JSON.stringify(draft));
    }

    async getDraft(): Promise<IMessageDraft | null> {
        const stored = localStorage.getItem(this.draftsKey);
        return stored ? JSON.parse(stored) : null;
    }

    async clearDraft(): Promise<void> {
        localStorage.removeItem(this.draftsKey);
    }

    async deleteMessage(messageId: string): Promise<void> {
        const stored = localStorage.getItem(this.storageKey);
        const messages = stored ? JSON.parse(stored) : [];
        const filteredMessages = messages.filter((msg: IMessage) => msg.id !== messageId);
        localStorage.setItem(this.storageKey, JSON.stringify(filteredMessages));
    }
}

export const mockMessageService = new MockMessageService();
